import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { UseFormReturn } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'

const modules = {
	toolbar: [
		[{ header: [1, 2, 3, 4, 5, 6, false] }],
		['bold', 'italic', 'underline', 'strike'],
		[{ color: [] }, { background: [] }],
		[{ list: 'ordered' }, { list: 'bullet' }],
		[{ align: [] }],
		['clean'],
	],
}

const formSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกหัวข้อประกาศ'),
	content: z.string().min(1, 'กรุณากรอกเนื้อหาประกาศ'),
	isActive: z.boolean().default(true),
})

export type FormValues = z.infer<typeof formSchema>

interface Props {
	defaultValues?: Partial<FormValues>
	onSubmit: (values: FormValues, form: UseFormReturn<FormValues>) => void
	mode: 'create' | 'edit'
}

export function AnnouncementForm({ defaultValues, onSubmit, mode }: Props) {
	const navigate = useNavigate()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			title: defaultValues?.title || '',
			content: defaultValues?.content || '',
			isActive: defaultValues?.isActive || true,
		},
	})

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const isValid = await form.trigger()
		if (!isValid) return
		const values = form.getValues()
		onSubmit(values, form)
	}

	return (
		<Form {...form}>
			<form onSubmit={handleSubmit} className="space-y-8">
				<div className="space-y-8">
					<FormField
						control={form.control}
						name="title"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> หัวข้อประกาศ
								</FormLabel>
								<FormControl>
									<Input placeholder="กรอกหัวข้อประกาศ" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="content"
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									<RequiredIndicator /> เนื้อหาประกาศ
								</FormLabel>
								<FormControl>
									<ReactQuill
										theme="snow"
										modules={modules}
										value={field.value}
										onChange={field.onChange}
										placeholder="กรอกเนื้อหาประกาศ"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex gap-4">
						<Button type="submit" variant="default">
							{mode === 'create' ? 'สร้างประกาศ' : 'แก้ไขประกาศ'}
						</Button>
						<Button
							type="button"
							variant="outline"
							onClick={() => navigate(-1)}
						>
							ยกเลิก
						</Button>
					</div>
				</div>
			</form>
		</Form>
	)
}
